/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { useGameLoop } from './useGameLoop';
import { useGameUiToggles } from './useGameUiToggles';
import { useLayerState } from './useLayerState';
import { useGameCoreState } from './useGameCoreState';
import { useGamePolicyBundle } from './useGamePolicyBundle';
import { useGameInputSetup } from './useGameInputSetup';
import { useGameSessionBundle } from './useGameSessionBundle';
import { useGameWorldActions } from './useGameWorldActions';
import { useGameNarrativeSetup } from './useGameNarrativeSetup';
import { uiStore, useUiStore } from '../stores/uiStore';

export function useGameApp() {
  const {
    showLanding,
    setShowLanding,
    currentPage,
    setCurrentPage,
    gameStarted,
    setGameStarted,
    gameStatus,
    setGameStatus,
    aiEnabled,
    setAiEnabled,
    grid,
    setGrid,
    gameSpeed,
    setGameSpeed,
    stats,
    setStats,
    research,
    setResearch,
    deltas,
    simBreakdown,
    milestones,
    setMilestones,
    tickNumber,
  } = useGameCoreState();

  const {
    showPerformanceMonitor,
    togglePerformanceMonitor,
    showEnhancedObjectives,
    toggleEnhancedObjectives,
    orgManagementOpen,
    setOrgManagementOpen,
  } = useGameUiToggles();

  const { activeModal } = useUiStore();
  const setActiveModal = uiStore.setActiveModal;
  const { layerManager, activeLayer, layerVisibility } = useLayerState();

  const narrative = useGameNarrativeSetup({
    aiEnabled,
    stats,
    grid,
    setGrid,
    setGameStatus,
    setGameStarted,
    setStats,
    setResearch,
    setMilestones,
    setActiveModal,
    gameStatus,
  });

  const world = useGameWorldActions({
    grid,
    setGrid,
    stats,
    setStats,
    research,
    setResearch,
    gameStarted,
    gameStatus,
    activeLayer,
    addNewsItem: narrative.addNewsItem,
    activeEvent: narrative.activeEvent,
    setActiveEvent: narrative.setActiveEvent,
  });

  useGameInputSetup({
    showLanding,
    gameStarted,
    activeModal,
    activeEvent: narrative.activeEvent,
    selectedTool: world.selectedTool,
    selectedTile: world.selectedTile,
    setActiveModal,
    setGameSpeed,
    setSelectedTool: world.setSelectedTool,
    setSelectedTile: world.setSelectedTile,
  });

  const session = useGameSessionBundle({
    newsFeed: narrative.newsFeed,
    setNewsFeed: narrative.setNewsFeed,
    currentGoal: narrative.currentGoal,
    setCurrentGoal: narrative.setCurrentGoal,
    statsDay: stats.day,
    activeScenario: narrative.activeScenario,
    gameStarted,
    gameStatus,
    stats,
    grid,
    research,
    gameSpeed,
    setStats,
    setResearch,
    setGrid,
    setMilestones,
    setGameStatus,
    setGameStarted,
    setAiEnabled,
    setShowLanding,
    setActiveModal,
    setActiveScenario: narrative.setActiveScenario,
    setGameSpeed,
    triggerStory: narrative.triggerStory,
    addNewsItem: narrative.addNewsItem,
    loadScenario: narrative.loadScenario,
  });

  const policy = useGamePolicyBundle(narrative.addNewsItem);

  useGameLoop(
    {
      gameStarted,
      gameStatus,
      activeQuiz: narrative.activeQuiz,
      activeEvent: narrative.activeEvent,
      activeModal,
      orgManagementOpen,
      gameSpeed,
    },
    {
      activeScenario: narrative.activeScenario,
      activeEvent: narrative.activeEvent,
      addNewsItem: narrative.addNewsItem,
      setPolicies: policy.setPolicies,
      setPolicyChallenges: policy.setPolicyChallenges,
      setPolicyEffects: policy.setPolicyEffects,
      setActiveEvent: narrative.setActiveEvent,
      setScenarioObjectives: narrative.setScenarioObjectives,
      fetchNews: narrative.fetchNews,
    }
  );

  return {
    showLanding,
    currentPage,
    setShowLanding,
    setCurrentPage,
    gameStarted,
    gameStatus,
    grid,
    stats,
    deltas,
    simBreakdown,
    milestones,
    tickNumber,
    research,
    gameSpeed,
    setGameSpeed,
    selectedTool: world.selectedTool,
    setSelectedTool: world.setSelectedTool,
    selectedTile: world.selectedTile,
    setSelectedTile: world.setSelectedTile,
    selectedTileData: world.selectedTileData,
    handleTileClick: world.handleTileClick,
    handleUpgrade: world.handleUpgrade,
    handleDelete: world.handleDelete,
    getPlacementError: world.getPlacementError,
    hasJumpGate: world.hasJumpGate,
    currentGoal: narrative.currentGoal,
    newsFeed: narrative.newsFeed,
    isGeneratingGoal: narrative.isGeneratingGoal,
    aiEnabled,
    currentStory: narrative.currentStory,
    activeEvent: narrative.activeEvent,
    setActiveEvent: narrative.setActiveEvent,
    activeScenario: narrative.activeScenario,
    scenarioObjectives: narrative.scenarioObjectives,
    activeQuiz: narrative.activeQuiz,
    setActiveQuiz: narrative.setActiveQuiz,
    activeModal,
    setActiveModal,
    activeLayer,
    layerVisibility,
    showPerformanceMonitor,
    togglePerformanceMonitor,
    orgManagementOpen,
    setOrgManagementOpen,
    showEnhancedObjectives,
    toggleEnhancedObjectives,
    policies: policy.policies,
    policyChallenges: policy.policyChallenges,
    policyMilestones: policy.policyMilestones,
    activePolicy: policy.activePolicy,
    setActivePolicy: policy.setActivePolicy,
    policyEffects: policy.policyEffects,
    handleStart: session.handleStart,
    handleResume: session.handleResume,
    handleSaveNow: session.handleSaveNow,
    hasSave: session.hasSave,
    savedGameAvailable: session.savedGameAvailable,
    handleSoftReset: session.handleSoftReset,
    handleRetryScenario: session.handleRetryScenario,
    handleExtend: session.handleExtend,
    triggerStory: narrative.triggerStory,
    fetchNewGoal: narrative.fetchNewGoal,
    handleTakeLoan: world.handleTakeLoan,
    handleRepayLoan: world.handleRepayLoan,
    handlePitchInvestors: world.handlePitchInvestors,
    handleLaunchProduct: world.handleLaunchProduct,
    handleStartResearch: world.handleStartResearch,
    handleSOCAction: world.handleSOCAction,
    handleAskAICoach: narrative.handleAskAICoach,
    handleAutoSolve: narrative.handleAutoSolve,
    handleClaimReward: narrative.handleClaimReward,
    handleCreatePolicy: policy.handleCreatePolicy,
    handleSubmitPolicyForReview: policy.handleSubmitPolicyForReview,
    handleApprovePolicy: policy.handleApprovePolicy,
    handleImplementPolicy: policy.handleImplementPolicy,
    handleEnforcePolicy: policy.handleEnforcePolicy,
    handleActivateChallenge: policy.handleActivateChallenge,
    handlePolicyAudit: policy.handlePolicyAudit,
    layerManager,
  };
}